package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.MultiPressContent
import com.shadowings.gero.domain.model.MultiPressPair
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.quizSelectionCorrectBg
import com.shadowings.gero.presentation.ui.quizSelectionPendingBg
import com.shadowings.gero.presentation.ui.quizSelectionWrongBg
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.util.SoundPlayer
import com.shadowings.gero.util.rememberHapticVibration
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.share_quiz_word
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Composable
private fun MultiPressComposablePreview() {
    AppTheme {
        Surface {
            MultiPressComposable(
                quiz = MultiPressContent(
                    pairs = listOf(
                        MultiPressPair("M17", "acqua"),
                        MultiPressPair("N5", "sole"),
                        MultiPressPair("D54", "gamba"),
                        MultiPressPair("G43", "quaglia")
                    )
                ),
                comboCount = remember { mutableStateOf(0) },
                quizList = remember { mutableStateOf(emptyList()) },
                page = 0,
                pagerState = androidx.compose.foundation.pager.rememberPagerState { 1 },
                navController = rememberNavController(),
                quizResults = remember { mutableStateOf(emptyMap()) },
                errorsCount = remember { mutableStateOf(0) },
                userData = UserData(),
            )
        }
    }
}

@Composable
fun MultiPressComposable(
    quiz: MultiPressContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    userData: UserData,
    lessonId: String = "",
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    // Deliberately no onAnswerResult param here (unlike every other quiz content composable):
    // this type's `state.onConfirm` only ever fires with isCorrect = true, once all pairs are
    // matched (see the LaunchedEffect below) - a wrong single-pair tap only removes a heart
    // (onNextQuestion(false)) without marking the whole question wrong, and the user always
    // finishes by matching every pair correctly. There is no "answered this question wrong and
    // moved on" moment to record into the mistakes list for this type.
) {
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val hapticFeedback = LocalHapticFeedback.current
    val vibrate = rememberHapticVibration()
    val rightOrder = rememberSaveable(quiz.id) { quiz.pairs.indices.shuffled() }

    var selectionState by rememberSaveable(quiz.id, stateSaver = MultiPressSelectionStateSaver) {
        mutableStateOf(MultiPressSelectionState())
    }

    LaunchedEffect(selectionState.wrongPair) {
        if (selectionState.wrongPair != null) {
            delay(420)
            selectionState = clearMultiPressWrongPair(selectionState)
        }
    }

    LaunchedEffect(selectionState.pairResult, selectionState.shouldConfirm) {
        when (selectionState.pairResult) {
            MultiPressPairResult.WRONG -> {
                SoundPlayer.playIncorrect()
                vibrate(300)
                onNextQuestion?.invoke(false)
            }

            MultiPressPairResult.CORRECT -> {
                // L'ultima coppia corretta attiva gia il feedback completo tramite onConfirm().
                if (!selectionState.shouldConfirm) {
                    SoundPlayer.playCorrect()
                    vibrate(50)
                }
            }

            null -> Unit
        }
    }

    LaunchedEffect(selectionState.shouldConfirm) {
        if (selectionState.shouldConfirm && state.pending) {
            state.onConfirm(isCorrect = true)
            selectionState = selectionState.copy(shouldConfirm = false)
        }
    }

    val deviceInfo = rememberDeviceInfo()
    val isLandscape = deviceInfo.isLandscape

    Box(modifier = Modifier.fillMaxSize()) {

        val promptText = quiz.prompt.resolve()

        val questionContent = @Composable {
            QuestionBox(
                question = promptText,
                modifier = Modifier.padding(vertical = 12.dp)
            )
        }

        val optionsContent = @Composable {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    quiz.pairs.forEachIndexed { index, pair ->
                        val isMatched = index in selectionState.matchedLeft
                        val isSelected = selectionState.selectedLeft == index
                        val isWrong = selectionState.wrongPair?.first == index
                        MultiPressCard(
                            text = pair.hieroglyphs,
                            showHieroglyphs = true,
                            selected = isSelected,
                            matched = isMatched,
                            wrong = isWrong,
                            enabled = state.pending && !isMatched,
                            tag = pair.hieroglyphs,
                            onClick = {
                                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                selectionState = onMultiPressTap(
                                    state = selectionState,
                                    column = MultiPressColumn.LEFT,
                                    index = index,
                                    rightToLeftMap = rightOrder,
                                    totalPairs = quiz.pairs.size,
                                )
                            }
                        )
                    }
                }
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    rightOrder.forEachIndexed { rightIndex, leftIndex ->
                        val isMatched = rightIndex in selectionState.matchedRight
                        val isSelected = selectionState.selectedRight == rightIndex
                        val isWrong = selectionState.wrongPair?.second == rightIndex
                        MultiPressCard(
                            text = quiz.pairs[leftIndex].meaning,
                            showHieroglyphs = false,
                            selected = isSelected,
                            matched = isMatched,
                            wrong = isWrong,
                            enabled = state.pending && !isMatched,
                            tag = quiz.pairs[leftIndex].meaning,
                            onClick = {
                                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                selectionState = onMultiPressTap(
                                    state = selectionState,
                                    column = MultiPressColumn.RIGHT,
                                    index = rightIndex,
                                    rightToLeftMap = rightOrder,
                                    totalPairs = quiz.pairs.size,
                                )
                            }
                        )
                    }
                }
            }
        }

        QuizContentScaffold(
            testTag = "multipress_content",
            isLandscape = isLandscape,
            questionContent = { questionContent() },
            optionsContent = { optionsContent() }
        )

        val correctAnswer = quiz.pairs.joinToString("\n") { "${it.hieroglyphs} -> ${it.meaning}" }
        val userAnswer = rightOrder
            .filterIndexed { rightIndex, _ -> rightIndex in selectionState.matchedRight }
            .joinToString("\n") { leftIndex -> "${quiz.pairs[leftIndex].hieroglyphs} -> ${quiz.pairs[leftIndex].meaning}" }
            .ifBlank { "Nessuna coppia" }
        val quizInfo = """
            Quiz ID: ${quiz.id}
            Type: MultiPress Quiz
            Prompt: ${quiz.prompt.resolve()}
            Pairs: ${quiz.pairs.joinToString { "${it.hieroglyphs} -> ${it.meaning}" }}
        """.trimIndent()

        QuizBottomFeedbackAndButton(
            modifier = Modifier.align(Alignment.BottomCenter),
            state = state,
            buttonEnabled = !state.pending && !state.blockClicks,
            correctAnswer = correctAnswer,
            userData = userData,
            lessonId = lessonId,
            quizInfo = quizInfo,
            userAnswer = userAnswer,
            shareText = stringResource(Res.string.share_quiz_word),
            onConfirmClick = { true },
        )
    }
}

@Composable
private fun MultiPressCard(
    text: String,
    showHieroglyphs: Boolean,
    selected: Boolean,
    matched: Boolean,
    wrong: Boolean,
    enabled: Boolean,
    tag: String,
    onClick: () -> Unit,
) {
    val background = when {
        matched -> quizSelectionCorrectBg
        wrong -> quizSelectionWrongBg
        selected -> quizSelectionPendingBg
        else -> MaterialTheme.colorScheme.surfaceVariant
    }

    val border = when {
        matched -> MaterialTheme.colorScheme.primary
        wrong -> MaterialTheme.colorScheme.error
        selected -> MaterialTheme.colorScheme.primary
        else -> MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
    }

    OutlinedCard(
        onClick = onClick,
        enabled = enabled,
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.outlinedCardColors(
            containerColor = background,
            contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
            disabledContainerColor = background,
            disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant,
        ),
        border = BorderStroke(if (selected || matched || wrong) 2.dp else 1.dp, border),
        modifier = Modifier
            .padding(4.dp)
            .fillMaxWidth()
            .height(84.dp)
            .semantics { testTag = tag }
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            if (showHieroglyphs) {
                MdCWord(word = text, rtl = false, cart = false, size = 46.dp)
            } else {
                Text(
                    text = text,
                    style = MaterialTheme.typography.bodyLarge,
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}




